import { useEffect } from 'react';
import { getEmbedAwarePath, useEmbedStore } from '../embedStore';

/**
 * Fallback recovery for embed mode: detects a stale/broken embed state
 * that occurs when the iframe has been idle (hidden tab, user navigated away
 * from the Arsenal page, etc.) and React's DOM height collapses to near-zero
 * while the app is still mounted.
 *
 * A simple page reload (F5) always fixes this. This hook reproduces that
 * behavior automatically — but ONLY when a genuinely abnormal state is detected.
 *
 * Anti-loop: uses sessionStorage to prevent repeated reloads.
 */

const RECOVERY_STAGE_KEY = 'dp_embed_recovery_stage';
const EMBED_ERROR_SCREEN_SELECTOR = '[data-embed-error-screen="true"]';
const STAGE_RELOADED = 'reloaded';
const STAGE_FALLBACK_DONE = 'fallback_done';

function hasEmbedQueryParam(): boolean {
  try {
    const embed = new URLSearchParams(window.location.search).get('embed');
    return embed === '1' || embed === 'true';
  } catch {
    return false;
  }
}

function isEmbedErrorScreenVisible(): boolean {
  return !!document.querySelector(EMBED_ERROR_SCREEN_SELECTOR);
}

function isEmbedRuntimeBroken(): boolean {
  if (document.visibilityState === 'hidden') {
    return false;
  }

  if (isEmbedErrorScreenVisible()) {
    return true;
  }

  const appRoot = document.getElementById('root');
  if (!appRoot) {
    return true;
  }

  if (appRoot.childElementCount === 0) {
    return true;
  }

  return false;
}

function triggerEmbedRecovery() {
  try {
    const stage = sessionStorage.getItem(RECOVERY_STAGE_KEY);

    if (stage === STAGE_RELOADED) {
      sessionStorage.setItem(RECOVERY_STAGE_KEY, STAGE_FALLBACK_DONE);
      if (window.location.pathname !== '/my-office') {
        window.location.replace(getEmbedAwarePath('/my-office'));
      }
      return;
    }

    if (stage === STAGE_FALLBACK_DONE) {
      return;
    }

    sessionStorage.setItem(RECOVERY_STAGE_KEY, STAGE_RELOADED);
    window.location.reload();
  } catch {
    // ignore sessionStorage access errors
  }
}

export function useEmbedRecovery() {
  const isEmbed = useEmbedStore((state) => state.isEmbed);

  useEffect(() => {
    if (!isEmbed || typeof window === 'undefined' || window.parent === window || !hasEmbedQueryParam()) return;

    const evaluateRecovery = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      if (isEmbedRuntimeBroken()) {
        triggerEmbedRecovery();
        return;
      }

      try {
        if (sessionStorage.getItem(RECOVERY_STAGE_KEY)) {
          sessionStorage.removeItem(RECOVERY_STAGE_KEY);
        }
      } catch {
        // ignore sessionStorage access errors
      }
    };

    let checkScheduled = false;
    let cancelled = false;
    let outerRafId = 0;
    let innerRafId = 0;
    const scheduleRecoveryCheck = () => {
      if (checkScheduled) return;
      checkScheduled = true;
      outerRafId = window.requestAnimationFrame(() => {
        if (cancelled) return;
        innerRafId = window.requestAnimationFrame(() => {
          if (cancelled) return;
          checkScheduled = false;
          evaluateRecovery();
        });
      });
    };

    scheduleRecoveryCheck();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleRecoveryCheck();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        scheduleRecoveryCheck();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', scheduleRecoveryCheck);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(outerRafId);
      window.cancelAnimationFrame(innerRafId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', scheduleRecoveryCheck);
    };
  }, [isEmbed]);
}
